/**
 * Cart drawer — the "added to cart" panel, fixed to the right edge.
 *
 * A native <dialog> via showModal(), as the garage modal is, so Escape, the
 * top layer and focus containment come from the browser. The markup inside is
 * Liquid (sections/cart-drawer.liquid): after any cart change cart.js asks
 * Shopify to re-render the section in the same request and the drawer swaps
 * its contents for the result, so what it shows is always the real cart.
 *
 * Only the inner content is swapped, never the dialog itself — replacing an
 * open dialog would close it.
 */
(function () {
  'use strict';

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cart-drawer-dialog]');
      this.sectionId = this.dataset.cartSection;
      this.lastAdded = null;
      if (!this.dialog) return;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-drawer-close]')) {
          this.close();
        } else if (event.target === this.dialog) {
          // The dialog's own box is the panel; a click landing on the dialog
          // element itself is the backdrop.
          this.close();
        }
      });

      // Closed by Escape, the backdrop or the button: the next open starts
      // without a stale "Added to your cart".
      this.dialog.addEventListener('close', () => {
        this.lastAdded = null;
        this.markAdded();
      });

      document.addEventListener('cart:updated', (event) => this.onCartUpdated(event.detail || {}));

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-drawer-open]');
        if (!opener) return;
        event.preventDefault();
        this.open();
        // Another tab may have changed the cart since this page rendered.
        this.refresh();
      });
    }

    get content() {
      return this.querySelector('[data-cart-drawer-content]');
    }

    open(added) {
      if (!this.dialog.open) this.dialog.showModal();
      if (added) this.lastAdded = added;
      this.markAdded();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    /** Shows the "Added" banner and outlines the lines that were just added. */
    markAdded() {
      const added = this.lastAdded;
      const banner = this.querySelector('[data-cart-drawer-added]');
      if (banner) banner.hidden = !added;
      for (const line of this.querySelectorAll('[data-cart-line]')) {
        const hit = !!added && added.some((item) => String(item.id) === line.dataset.variantId);
        line.toggleAttribute('data-just-added', hit);
      }
    }

    render(html) {
      const next = new DOMParser().parseFromString(html, 'text/html')
        .querySelector('[data-cart-drawer-content]');
      const current = this.content;
      if (!next || !current) return;
      if (window.Cart) window.Cart.swap(current, next);
      else current.replaceWith(next);
      this.markAdded();
    }

    async refresh() {
      try {
        // Plain GET: an Accept: application/json header makes Shopify serve the
        // URL's JSON resource instead of the requested sections.
        const res = await fetch(`${root()}?sections=${encodeURIComponent(this.sectionId)}`);
        if (!res.ok) return;
        const json = await res.json();
        if (json[this.sectionId]) this.render(json[this.sectionId]);
      } catch {
        /* keep what is there */
      }
    }

    onCartUpdated(detail) {
      const html = detail.sections && detail.sections[this.sectionId];
      if (html) this.render(html);
      else this.refresh();

      if (detail.source === 'add' && detail.open !== false) this.open(detail.added);
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
})();
